import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Mountain, Waves, Sunrise, Compass } from "lucide-react";
import Reveal from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import EditorialMarquee from "@/components/EditorialMarquee";

const chapters = [
  {
    num: "01",
    title: "RESET",
    icon: Waves,
    text: "Step away from the constant noise. Through guided breathwork and cold water immersion, you learn to regulate your nervous system and handle stress effortlessly.",
    points: ["Breathwork that rewires your stress response", "Cold water immersion with expert guidance", "A clear mind you can return to daily"],
    img: "/images/rx4/breathwork-firepit.jpg",
    alt: "RX4 breathwork session around the fire pit",
  },
  {
    num: "02",
    title: "REFOCUS",
    icon: Compass,
    text: "Get clear on your goals and aspirations. Focus on what you can control, release what you cannot, and let your own experiences become the inspiration.",
    points: ["Clarity on what actually matters", "Goal setting, and goal getting", "Walking and talking in wild places"],
    img: "/images/rx4/group-hike.jpg",
    alt: "RX4 group walking a misty valley path",
  },
  {
    num: "03",
    title: "RECHARGE",
    icon: Mountain,
    text: "Reap the rewards of connecting with nature. Location has energy and time has memory, so we train in places that give something back.",
    points: ["Build genuine mental stability", "Unplug and de-stress properly", "Reconnect with people and place"],
    img: "/images/rx4/community-dip.webp",
    alt: "Community cold water dip in the lake",
  },
  {
    num: "04",
    title: "RELAUNCH",
    icon: Sunrise,
    text: "Establish daily routines, habits and structure that stick. Leave with a step-by-step plan and the systems to sustain a balanced life you love.",
    points: ["Daily habits that hold under pressure", "Simple, sustainable systems", "Lead with clarity and purpose"],
    img: "/images/rx4/dave-summit.webp",
    alt: "Dave with arms raised on a fell at sunset",
  },
];

const benefits = [
  { icon: Mountain, title: "Build mental stability", text: "Simple, sustainable systems to support a balanced life you love." },
  { icon: Waves, title: "Connect with nature", text: "Cold water, breathwork and time outdoors with proven rewards." },
  { icon: Sunrise, title: "Establish daily routines", text: "Habits and structure that make every area of life fulfilling." },
  { icon: Compass, title: "Live a healthier life", text: "Efficient and effective ways of functioning, daily and weekly." },
];

const Method = () => (
  <main data-testid="method-page">
    <PageHero
      testId="method-hero"
      overline="The RX4 Method"
      lines={["Four movements.", "One balanced life."]}
      sub="Tried and tested principles, delivered through systems, patterns and processes in line with the elements. Keep scrolling: each movement takes over the screen."
      image="/images/rx4/breathwork-lakeside.webp"
      alt="RX4 breathwork session on the lakeshore"
    />

    {/* FULL-SCREEN CHAPTERS */}
    <div data-testid="method-chapters">
      {chapters.map((c) => (
        <section key={c.num} className="sticky top-0 h-screen flex items-end overflow-hidden" data-testid={`method-chapter-${c.num}`}>
          <div className="absolute inset-0">
            <img src={c.img} alt={c.alt} className="w-full h-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-black/55" />
            <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-ink to-transparent" />
          </div>
          <div className="relative z-10 px-6 md:px-12 lg:px-20 pb-16 md:pb-24 max-w-7xl mx-auto w-full">
            <div className="flex items-center gap-4 mb-6">
              <c.icon size={22} className="text-brand" strokeWidth={1.75} />
              <span className="font-mono text-sm text-brand">[ {c.num} / 04 ]</span>
            </div>
            <h2 className="font-display font-bold tracking-tight leading-none text-6xl sm:text-7xl lg:text-9xl text-bone">{c.title}</h2>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8 items-end">
              <p className="text-base sm:text-lg text-slate-200 leading-relaxed max-w-xl">{c.text}</p>
              <ul className="space-y-3">
                {c.points.map((p, i) => (
                  <li key={i} className="flex items-start gap-3 text-slate-300 text-sm" data-testid={`method-chapter-${c.num}-point-${i}`}>
                    <Check size={16} className="text-brand mt-0.5 shrink-0" strokeWidth={2.5} /> {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ))}
    </div>

    <EditorialMarquee items={["LOCATION HAS ENERGY", "TIME HAS MEMORY"]} />

    {/* WHY IT WORKS */}
    <section className="section-shell" data-testid="method-benefits">
      <Reveal className="mb-14">
        <p className="overline-label mb-4">Why it works</p>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-bone max-w-2xl">
          Simple and sustainable, by design.
        </h2>
        <p className="mt-4 text-slate-400 max-w-xl">Once you know what to do, changing your life is 100% accessible. These are the rewards our clients report most.</p>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {benefits.map((b, i) => (
          <Reveal key={b.title} delay={i * 0.08}>
            <div data-testid={`method-benefit-${i}`} className="bg-ink-soft border border-white/5 rounded-2xl p-7 h-full transition-all duration-300 hover:border-brand/40">
              <b.icon size={24} className="text-brand mb-5" strokeWidth={1.75} />
              <h3 className="font-display font-semibold text-bone mb-2">{b.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{b.text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    {/* CTA */}
    <section className="bg-ink-soft border-t border-white/5" data-testid="method-cta">
      <div className="section-shell text-center">
        <Reveal>
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-bone max-w-2xl mx-auto leading-[1.1]">
            Experience the method for yourself.
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/apply" data-testid="method-cta-apply" className="btn-primary group">
              Start Your Journey
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            <Link to="/experiences" data-testid="method-cta-experiences" className="btn-secondary">
              Explore Experiences
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  </main>
);

export default Method;
